import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import {
  Package,
  Clock,
  CheckCircle,
  XCircle,
  MagnifyingGlass,
  Plus,
  Eye,
  CheckSquare,
  Square,
  DownloadSimple,
  TrendUp,
  Percent,
  Spinner,
  Warning,
  MapPin,
  Airplane,
  Boat,
  Truck,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import {
  typography,
  spacing,
  components,
  getInitials,
  getActivityIndicator,
} from '../../styles/design-system'
import { formatDateBR, daysUntil, isExpired } from '../../lib/date'
import { useExchangeRate, formatCurrency as formatCurrencyUtil } from '../../hooks/useExchangeRate'
import { AgentReputation } from '../../components/AgentReputation'
import { MultiCurrencyTotal } from '../../components/offer/MultiCurrencyTotal'
import { totalsFromLegacyOffer } from '../../utils/offerTotals'

interface Offer {
  id: string
  quoteId: string
  quoteReference: string
  clientReference?: string | null
  origin?: {
    location?: { city?: string; country?: string }
  }
  destination?: {
    location?: { city?: string; country?: string }
  }
  incoterm?: string
  modalityName?: string
  modalityType?: string
  freightForwarderId: string
  agentName: string
  agentTradingName?: string
  agentLogoUrl?: string
  agentAvgRating?: number
  agentReviewCount?: number
  agentCompletedCount?: number
  totalValue: number
  totalsByCurrency?: Record<string, number> | null
  currency: string
  deliveryTime?: string
  freeTimeDays?: number | null
  validityDate?: string
  status: 'sent' | 'accepted' | 'declined'
  createdAt: string
  sentAt?: string
  isBestOffer?: boolean
  savingsPercentage?: number | null
  isBestTransit?: boolean
  isBestFreeTime?: boolean
  breakdown?: any
}

interface Metrics {
  pending: number
  accepted: number
  declined: number
  total: number
  avgValue: number
  minValue: number
  maxValue: number
  expired: number
  expiringSoon: number
  thisMonth: number
}

export default function OffersPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { rate, preferredCurrency, convertUSDToBRL, convertBRLToUSD, loading: rateLoading } = useExchangeRate({ profileType: 'shipper' })

  // Debug log in development
  if (import.meta.env.DEV) {
    console.log('Exchange rate state:', { rate, preferredCurrency, rateLoading })
  }

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [offers, setOffers] = useState<Offer[]>([])
  const [metrics, setMetrics] = useState<Metrics | null>(null)
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 })

  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState('sent') // Padrão: apenas pendentes
  const [filterModality, setFilterModality] = useState('all')
  const [offerSort, setOfferSort] = useState<'none' | 'price' | 'transit' | 'freetime' | 'expiring'>('none')
  const [selectedOffers, setSelectedOffers] = useState<string[]>([])

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Fetch offers from API
  const fetchOffers = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Construir query params manualmente (api service não suporta { params })
      const queryParams = new URLSearchParams()
      queryParams.set('limit', String(pagination.limit))
      queryParams.set('offset', String(pagination.offset))
      queryParams.set('status', filterStatus)
      if (filterModality !== 'all') queryParams.set('modality', filterModality)
      if (debouncedSearch) queryParams.set('search', debouncedSearch)

      const url = `/shipper/offers?${queryParams.toString()}`
      const response = await api.get(url)

      setOffers(response.data.offers)
      setMetrics(response.data.metrics)
      setPagination(response.data.pagination)
    } catch (err: any) {
      console.error('Error fetching offers:', err)
      setError(err.response?.data?.error || 'Erro ao carregar ofertas')
    } finally {
      setLoading(false)
    }
  }, [filterStatus, filterModality, debouncedSearch, pagination.limit, pagination.offset])

  useEffect(() => {
    fetchOffers()
  }, [fetchOffers])

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedOffers.length === offers.length) {
      setSelectedOffers([])
    } else {
      setSelectedOffers(offers.map((o) => o.id))
    }
  }

  const toggleSelectOffer = (offerId: string) => {
    setSelectedOffers((prev) =>
      prev.includes(offerId)
        ? prev.filter((id) => id !== offerId)
        : [...prev, offerId]
    )
  }

  const isOfferSelected = (offerId: string) => selectedOffers.includes(offerId)
  const isAllSelected = offers.length > 0 && selectedOffers.length === offers.length

  // Get status badge
  const getOfferStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      sent: 'bg-yellow-100 text-yellow-700',
      accepted: 'bg-emerald-100 text-emerald-700',
      declined: 'bg-red-100 text-red-700',
    }
    return `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badges[status] || 'bg-gray-100 text-gray-700'}`
  }

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      sent: 'Pendente',
      accepted: 'Aceita',
      declined: 'Recusada',
    }
    return labels[status] || status
  }

  // Format currency with preference conversion
  const formatCurrencyWithPreference = (value: number, offerCurrency: string) => {
    // If offer currency matches preference, just format
    if (offerCurrency === preferredCurrency) {
      return formatCurrencyUtil(value, offerCurrency)
    }

    // Convert based on preference (only USD <-> BRL conversion supported)
    if (offerCurrency === 'USD' && preferredCurrency === 'BRL') {
      if (rate) {
        const convertedValue = convertUSDToBRL(value)
        return formatCurrencyUtil(convertedValue, 'BRL')
      }
      // Rate not loaded yet, show original
      return formatCurrencyUtil(value, offerCurrency)
    }
    if (offerCurrency === 'BRL' && preferredCurrency === 'USD') {
      if (rate) {
        const convertedValue = convertBRLToUSD(value)
        return formatCurrencyUtil(convertedValue, 'USD')
      }
      // Rate not loaded yet, show original
      return formatCurrencyUtil(value, offerCurrency)
    }

    // Fallback: show in offer's original currency (EUR, GBP, CNY, etc.)
    return formatCurrencyUtil(value, offerCurrency)
  }

  // Format currency in original currency (for secondary display)
  const formatCurrency = (value: number, currency: string) => {
    return formatCurrencyUtil(value, currency)
  }

  // Calculate days until expiry
  const getDaysUntilExpiry = (validUntil?: string) => {
    if (!validUntil) return '-'
    const days = daysUntil(validUntil)
    if (days < 0) return 'Expirada'
    if (days === 0) return 'Expira hoje'
    if (days === 1) return '1 dia'
    return `${days} dias`
  }

  // Get modality icon
  const getModalityIcon = (modalityType?: string) => {
    switch (modalityType) {
      case 'air':
        return <Airplane size={14} className="text-blue-500" />
      case 'maritime':
        return <Boat size={14} className="text-cyan-500" />
      case 'ground':
        return <Truck size={14} className="text-amber-500" />
      default:
        return <Package size={14} className="text-gray-400" />
    }
  }

  // Client-side sort for the offers list (Plane #4). Offers missing the sorted
  // field sink to the bottom so a blank value never ranks first.
  const displayedOffers = (() => {
    if (offerSort === 'none') return offers
    const arr = offers.slice()
    const num = (v: any, fallback: number) => {
      const n = typeof v === 'number' ? v : parseFloat(v)
      return Number.isFinite(n) ? n : fallback
    }
    arr.sort((a, b) => {
      switch (offerSort) {
        case 'price':
          return num(a.totalValue, Infinity) - num(b.totalValue, Infinity)
        case 'transit':
          return num(a.deliveryTime, Infinity) - num(b.deliveryTime, Infinity)
        case 'freetime':
          return num(b.freeTimeDays, -Infinity) - num(a.freeTimeDays, -Infinity)
        case 'expiring': {
          const av = a.validityDate ? new Date(a.validityDate).getTime() : Infinity
          const bv = b.validityDate ? new Date(b.validityDate).getTime() : Infinity
          return av - bv // soonest expiry first
        }
        default:
          return 0
      }
    })
    return arr
  })()

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className={typography.pageTitle}>Propostas Recebidas</h1>
            </div>
            <div className="flex items-center gap-3">
              {selectedOffers.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-medium">{selectedOffers.length}</span>
                  selecionada{selectedOffers.length > 1 ? 's' : ''}
                </div>
              )}
              <Button
                onClick={() => navigate('/shipper/quotes/new')}
                size="default"
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus size={18} weight="bold" />
                Nova Cotacao
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <Warning size={20} className="text-red-500" />
            <span className="text-red-700">{error}</span>
            <Button variant="outline" size="sm" onClick={fetchOffers} className="ml-auto">
              Tentar novamente
            </Button>
          </div>
        )}

        {/* Metrics Zone - 3 Column Grid */}
        {metrics && (
          <div className="grid grid-cols-3 gap-6 mb-6">
            {/* Overview Card */}
            <div className={components.card}>
              <p className={typography.cardTitle}>Visao Geral</p>
              <div className="flex items-end justify-between mt-3">
                <div>
                  <p className={typography.metricValueAccent}>{metrics.pending}</p>
                  <p className={typography.metricLabel}>
                    Pendentes de {metrics.total} total
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    +{metrics.thisMonth} este mes
                  </p>
                </div>
                <Package size={32} className="text-emerald-600 opacity-80" weight="duotone" />
              </div>
            </div>

            {/* Financial Card */}
            <div className={components.card}>
              <p className={typography.cardTitle}>Economia & Precos</p>
              <div className="space-y-2 mt-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Menor preco</span>
                  <span className="font-semibold text-emerald-600 flex items-center gap-1">
                    <TrendUp size={14} weight="bold" />
                    {formatCurrency(metrics.minValue, 'USD')}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Preco medio</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(metrics.avgValue, 'USD')}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Maior preco</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(metrics.maxValue, 'USD')}
                  </span>
                </div>
              </div>
            </div>

            {/* Timing Card */}
            <div className={components.card}>
              <p className={typography.cardTitle}>Tempo & Status</p>
              <div className="flex items-end justify-between mt-3">
                <div>
                  <p className={typography.metricValue}>{metrics.accepted}</p>
                  <p className={typography.metricLabel}>Ofertas aceitas</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {metrics.declined} recusadas
                  </p>
                </div>
                <div className="text-right text-sm text-gray-600">
                  {metrics.expiringSoon > 0 && (
                    <div className="flex items-center gap-1 mb-1">
                      <Clock size={14} className="text-amber-500" />
                      <span>{metrics.expiringSoon} expirando em breve</span>
                    </div>
                  )}
                  {metrics.expired > 0 && (
                    <div className="flex items-center gap-1 text-red-500">
                      <Warning size={14} />
                      <span>{metrics.expired} expiradas</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filter Bar */}
        <div className={components.filterBar + ' mb-6'}>
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlass
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <Input
                type="text"
                placeholder="Buscar por cotacao ou agente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-9 border-gray-200"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className={components.filterInput}
            >
              <option value="all">Todos Status</option>
              <option value="sent">Pendentes</option>
              <option value="accepted">Aceitas</option>
              <option value="declined">Recusadas</option>
            </select>

            {/* Modality Filter */}
            <select
              value={filterModality}
              onChange={(e) => setFilterModality(e.target.value)}
              className={components.filterInput}
            >
              <option value="all">Todas Modalidades</option>
              <option value="maritime">Maritimo</option>
              <option value="air">Aereo</option>
              <option value="ground">Terrestre</option>
            </select>

            {/* Sort (Plane #4) */}
            <select
              value={offerSort}
              onChange={(e) => setOfferSort(e.target.value as typeof offerSort)}
              className={components.filterInput}
            >
              <option value="none">Ordenar por…</option>
              <option value="price">Menor valor</option>
              <option value="transit">Menor transit time</option>
              <option value="freetime">Maior free time</option>
              <option value="expiring">Expirando primeiro</option>
            </select>

            {/* Export */}
            <Button variant="outline" size="sm" className="gap-2 h-9">
              <DownloadSimple size={16} />
              Exportar
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size={32} className="animate-spin text-emerald-500" />
            <span className="ml-3 text-gray-500">Carregando ofertas...</span>
          </div>
        )}

        {/* Data Grid */}
        {!loading && (
          <div className={components.tableContainer}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={components.tableHeader}>
                  <tr>
                    <th className="w-12 px-4 py-3">
                      <button
                        onClick={toggleSelectAll}
                        className="flex items-center justify-center w-full"
                      >
                        {isAllSelected ? (
                          <CheckSquare size={18} weight="fill" className="text-emerald-600" />
                        ) : (
                          <Square size={18} className="text-gray-400" />
                        )}
                      </button>
                    </th>
                    <th className={`text-left ${spacing.tableHeaderPadding} ${typography.tableHeader}`}>
                      Cotacao / Agente
                    </th>
                    <th className={`text-left ${spacing.tableHeaderPadding} ${typography.tableHeader}`}>
                      Rota
                    </th>
                    <th className={`text-right ${spacing.tableHeaderPadding} ${typography.tableHeader}`}>
                      Valor
                    </th>
                    <th className={`text-left ${spacing.tableHeaderPadding} ${typography.tableHeader}`}>
                      Trânsito
                    </th>
                    <th className={`text-left ${spacing.tableHeaderPadding} ${typography.tableHeader}`}>
                      Free Time
                    </th>
                    <th className={`text-left ${spacing.tableHeaderPadding} ${typography.tableHeader}`}>
                      Validade
                    </th>
                    <th className={`text-left ${spacing.tableHeaderPadding} ${typography.tableHeader}`}>
                      Status
                    </th>
                    <th className={`text-right ${spacing.tableHeaderPadding} ${typography.tableHeader} sticky right-0 z-20 bg-gray-50 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]`}>
                      Acoes
                    </th>
                  </tr>
                </thead>
                <tbody className={components.tableDivider}>
                  {displayedOffers.map((offer) => (
                    <tr
                      key={offer.id}
                      className={`bg-white ${components.tableRow} ${
                        isOfferSelected(offer.id) ? components.tableRowSelected : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleSelectOffer(offer.id)}
                          className="flex items-center justify-center"
                        >
                          {isOfferSelected(offer.id) ? (
                            <CheckSquare size={18} weight="fill" className="text-emerald-600" />
                          ) : (
                            <Square size={18} className="text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={components.avatar}>
                            {offer.agentLogoUrl ? (
                              <img src={offer.agentLogoUrl} alt="" className="w-full h-full object-cover rounded-full" />
                            ) : (
                              <span className={components.avatarText}>
                                {getInitials(offer.agentTradingName || offer.agentName)}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); navigate(`/freight-forwarders/${offer.freightForwarderId}`) }}
                              className={`font-medium ${typography.tableCell} truncate hover:text-emerald-700 hover:underline text-left`}
                            >
                              {offer.agentTradingName || offer.agentName}
                            </button>
                            <AgentReputation
                              completedCount={offer.agentCompletedCount}
                              avgRating={offer.agentAvgRating}
                              reviewCount={offer.agentReviewCount}
                            />
                            <div className="flex items-center gap-2 flex-wrap mt-0.5">
                              <p className={`${typography.tableCellMono} text-xs`}>
                                {offer.quoteReference}
                              </p>
                              {offer.clientReference && (
                                <span className="text-xs text-gray-500">Ref. cliente: {offer.clientReference}</span>
                              )}
                              {getModalityIcon(offer.modalityType)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <MapPin size={12} />
                          <span className="truncate max-w-[100px]">
                            {offer.origin?.location?.city || '?'}
                          </span>
                          <span>→</span>
                          <span className="truncate max-w-[100px]">
                            {offer.destination?.location?.city || '?'}
                          </span>
                        </div>
                        {offer.incoterm && (
                          <span className="text-xs text-gray-400">{offer.incoterm}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex flex-col items-end">
                          <MultiCurrencyTotal
                            totalsByCurrency={totalsFromLegacyOffer(offer)}
                            variant="compact"
                          />
                          {offer.isBestOffer && offer.savingsPercentage && (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium mt-1">
                              <Percent size={10} weight="bold" />
                              Melhor -{offer.savingsPercentage}%
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-start">
                          <span className={typography.tableCellSecondary}>
                            {offer.deliveryTime || '-'}
                          </span>
                          {offer.isBestTransit && (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium mt-1">
                              <CheckCircle size={10} weight="bold" />
                              Melhor
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-start">
                          <span className={typography.tableCellSecondary}>
                            {offer.freeTimeDays ? `${offer.freeTimeDays} dias` : '-'}
                          </span>
                          {offer.isBestFreeTime && (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium mt-1">
                              <CheckCircle size={10} weight="bold" />
                              Melhor
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {offer.status === 'sent' && !isExpired(offer.validityDate) && offer.validityDate && (
                            <div {...getActivityIndicator(offer.validityDate)} />
                          )}
                          <span className={`${typography.tableCellSecondary} ${isExpired(offer.validityDate) ? 'text-red-500' : ''}`}>
                            {getDaysUntilExpiry(offer.validityDate)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={getOfferStatusBadge(offer.status)}>
                          {getStatusLabel(offer.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 sticky right-0 z-10 bg-inherit shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/shipper/quotes/${offer.quoteId}`)}
                            className="h-9 w-9 p-0"
                            title="Ver cotacao"
                          >
                            <Eye size={20} className="text-gray-600" />
                          </Button>
                          {offer.status === 'sent' && !isExpired(offer.validityDate) && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-9 px-3 text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Recusar"
                              >
                                <XCircle size={20} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-9 px-3 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                title="Aceitar"
                              >
                                <CheckCircle size={20} />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {offers.length === 0 && !loading && (
              <div className="text-center py-12 bg-gray-50">
                <Package size={48} className="mx-auto text-gray-300 mb-3" weight="duotone" />
                <p className="text-gray-500 text-sm">Nenhuma proposta encontrada</p>
                <p className="text-gray-400 text-xs mt-1">
                  Crie uma cotacao para receber propostas de agentes
                </p>
              </div>
            )}

            {/* Footer with pagination */}
            <div className={components.tableFooter + ' flex items-center justify-between'}>
              <div className="text-sm text-gray-700">
                <span className="font-medium">{pagination.total}</span> propostas
                {selectedOffers.length > 0 && (
                  <>
                    {' '}
                    · <span className="font-medium">{selectedOffers.length}</span> selecionada
                    {selectedOffers.length > 1 ? 's' : ''}
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <select className="px-2 py-1 border border-gray-200 rounded text-sm bg-white">
                  <option>50 por pagina</option>
                  <option>100 por pagina</option>
                  <option>200 por pagina</option>
                </select>
                <span className="text-sm text-gray-600">
                  Pagina {Math.floor(pagination.offset / pagination.limit) + 1} de{' '}
                  {Math.ceil(pagination.total / pagination.limit) || 1}
                </span>
              </div>
            </div>
          </div>
        )}
      </main>
    </AppLayout>
  )
}
